// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import LoginHome from 'pages/LoginHome';
import SignIn from 'components/SignIn';
import Dashboard from 'pages/Dashboard';
import './App.css';
import ClearImagesButton from 'utils/ClearImagesButton';
import Card from 'components/Card';
import Blog from 'components/support/Blog';
import DeleteUser from 'components/DeleteUser';
// Import accessory components
import AccessoryWorkFlow from 'components/accessory/AccessoryWorkFlow';
import AccessoryActivationFlow from 'components/accessory/AccessoryActivationFlow';
// Import debug helper for API call monitoring
import debugHelper from 'utils/debugHelper';

// TEAM PLATFORM ADDITION - Import the TeamDashboard component
import TeamDashboard from './team/pages/TeamDashboard';
import TeamMemberCard from './team/components/TeamMemberCard';

// TEAM V2 PLATFORM - Import the TeamV2Dashboard component
import TeamV2Dashboard from './team-v2/pages/TeamV2Dashboard';

// ADMIN - Import AdminContainer
import AdminContainer from './pages/AdminContainer';

// Import redirect component
import RedirectAfterLogin from './components/RedirectAfterLogin';
import { getLoginRedirectPath } from './utils/authUtils';



// ✅ CORRECTED: Import Sentry for ErrorBoundary + init function
import * as Sentry from "@sentry/react";
import { initSentry } from './utils/sentry';
import { onMessage } from 'firebase/messaging';
import { messaging } from './config/firebaseConfig';
initSentry();


// Simple error fallback component
function ErrorFallback() {
  return (
    <div style={{
      padding: '20px',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column'
    }}>
      <h2>Something went wrong</h2>
      <p>Please refresh the page or contact support.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginTop: '10px'
        }}
      >
        Refresh Page
      </button>
    </div>
  );
}

// ✅ SIMPLIFIED: ProtectedRoute component - only checks authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated() ? children : <Navigate to="/sign-in" />;
};

// AdminProtectedRoute - Only accessible to users with admin role
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, authState } = useAuth();

  // Check authentication first
  if (!isAuthenticated()) {
    return <Navigate to="/sign-in" />;
  }

  // Check if user has admin role (role name must include 'admin')
  // This matches the backend isAdminRole() logic
  const roleName = authState?.user?.roleId?.name || '';
  const isAdmin = roleName && roleName.toLowerCase().includes('admin');

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  // Only render if both checks pass
  return children;
};

// Team Protected Route - Only accessible to users with team plan
const TeamProtectedRoute = ({ children }) => {
  const { isAuthenticated, authState } = useAuth();

  // Check authentication first
  if (!isAuthenticated()) {
    return <Navigate to="/sign-in" />;
  }

  // Then check if user has team plan
  const isTeamUser = authState?.user?.plan === 'team';
  if (!isTeamUser) {
    return <Navigate to="/dashboard" />;
  }

  // Only render if both checks pass
  return children;
};

// Team Admin Route - Only accessible to team admins
const TeamAdminRoute = ({ children }) => {
  const { isAuthenticated, authState } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hasTeamAdminAccess, setHasTeamAdminAccess] = useState(false);

  useEffect(() => {
    let isActive = true;

    const checkTeamAdminAccess = async () => {
      if (!isAuthenticated()) {
        if (isActive) {
          setHasTeamAdminAccess(false);
          setIsLoading(false);
        }
        return;
      }

      // Non-team users never access /team
      if (authState?.user?.plan !== 'team') {
        if (isActive) {
          setHasTeamAdminAccess(false);
          setIsLoading(false);
        }
        return;
      }

      try {
        const path = await getLoginRedirectPath(authState.user, authState.token);
        if (isActive) {
          setHasTeamAdminAccess(path === '/team');
        }
      } catch (error) {
        console.error('Error checking team admin access:', error);
        if (isActive) {
          setHasTeamAdminAccess(false);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    checkTeamAdminAccess();

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, authState?.user, authState?.token]);

  if (!isAuthenticated()) {
    return <Navigate to="/sign-in" />;
  }

  if (isLoading) {
    return null;
  }

  return hasTeamAdminAccess ? children : <Navigate to="/dashboard" />;
};

const App = () => {
  const { loading, isAuthenticated, authState } = useAuth();

  // ✅ TEMPORARILY DISABLED: API call monitoring with debug helper to prevent infinite loops
  // useEffect(() => {
  //   console.log('🔍 Starting API call monitoring...');

  //   // Show first report after 15 seconds
  //   const timer1 = setTimeout(() => {
  //     console.log('📊 15-second report:');
  //     debugHelper.getReport();
  //   }, 15000);

  //   // Show second report after 30 seconds  
  //   const timer2 = setTimeout(() => {
  //     console.log('📊 30-second report:');
  //     debugHelper.getReport();
  //   }, 30000);

  //   return () => {
  //     clearTimeout(timer1);
  //     clearTimeout(timer2);
  //   };
  // }, []);

  // ✅ KEEP: Debug utilities for tracking
  useEffect(() => {
    // Create window-level debugging utilities for tracking
    window.debugTracking = {
      emitView: (cardId, source = 'qr_code') => {
        console.log(`Manual tracking triggered for card ${cardId}, source: ${source}`);

        // Try all known socket patterns
        const socket = window.io || window.socket || window._socket || window.insightsSocket?.socket;
        if (socket) {
          console.log('Found socket, emitting event');
          socket.emit('cardViewed', {
            cardId,
            viewSource: source,
            userId: 'manual_debug',
            timestamp: new Date().toISOString()
          });
        } else {
          console.log('No socket found, trying API');

          fetch(`/api/insights/track-view`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cardId,
              sourceType: source,
              userId: 'manual_debug'
            })
          });
        }
      },

      checkSockets: () => {
        console.log('Socket check:', {
          io: !!window.io,
          socket: !!window.socket,
          _socket: !!window._socket,
          insightsSocket: !!window.insightsSocket,
          insightsSocketConnected: window.insightsSocket?.connected
        });
      },

      // ✅ NEW: WebSocket connection test
      testWebSocket: () => {
        console.log('🧪 Testing WebSocket connection...');

        // Test basic WebSocket connection
        const ws = new WebSocket('wss://v2.api.intouchwe.com/socket.io/?EIO=4&transport=websocket');

        ws.onopen = () => {
          console.log('✅ WebSocket connection successful');
          ws.close();
        };

        ws.onerror = (error) => {
          console.error('❌ WebSocket connection failed:', error);
        };

        ws.onclose = () => {
          console.log('🔌 WebSocket connection closed');
        };
      },

      // ✅ NEW: Socket.IO connection test
      testSocketIO: () => {
        console.log('🧪 Testing Socket.IO connection...');

        const { io } = require('socket.io-client');
        const testSocket = io('https://v2.api.intouchwe.com', {
          transports: ['websocket', 'polling'],
          autoConnect: true
        });

        testSocket.on('connect', () => {
          console.log('✅ Socket.IO connection successful:', {
            id: testSocket.id,
            transport: testSocket.io.engine.transport.name
          });
          testSocket.disconnect();
        });

        testSocket.on('connect_error', (error) => {
          console.error('❌ Socket.IO connection failed:', error);
        });
      }
    };
  }, []);

  // ✅ KEEP: Global media error handling
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      // Check if it's a media-related error
      if (event.reason &&
        (event.reason.message?.includes('media') ||
          event.reason.message?.includes('camera') ||
          event.reason.message?.includes('scan') ||
          event.reason.message?.includes('play()') ||
          event.reason.message?.includes('clear'))) {

        // Prevent default error display
        event.preventDefault();
        console.warn('Media/scanner error suppressed:', event.reason);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Foreground push notifications (when app tab is open)
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return undefined;

    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload?.notification?.title || 'New notification';
      const options = {
        body: payload?.notification?.body || '',
        icon: '/logo192.png',
        data: payload?.data || {}
      };

      if (Notification.permission === 'granted') {
        const notification = new Notification(title, options);
        notification.onclick = () => {
          window.focus();
          const clickAction = payload?.data?.click_action;
          if (clickAction) window.location.href = clickAction;
        };
      }
    });

    return () => unsubscribe();
  }, []);

  // ✅ KEEP: Loading screen
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '20px',
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h3 style={{ margin: '0 0 10px', color: '#333' }}>Loading...</h3>
          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
            Checking authentication status...
          </p>
          <p style={{ margin: '10px 0 0', color: '#999', fontSize: '12px' }}>
            If this takes too long, check the browser console for errors
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
            
        `}</style>
      </div>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <Router>
        <Routes>
          {/* ✅ SIMPLIFIED: Signup flow - no guard, no special logic */}
          <Route path="/sign-up" element={<LoginHome />} />

          {/* ✅ SIMPLIFIED: Root path logic - redirect based on plan and role */}
          <Route path="/" element={
            isAuthenticated() ? <RedirectAfterLogin /> : <SignIn />
          } />

          <Route path="/sign-in" element={<SignIn />} />

          <Route path="/dashboard/*" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/clear-images" element={<ClearImagesButton />} />
          <Route path="/card/:cardId/*" element={<Card />} />
          <Route path="/profile/:profileId/*" element={<Card />} />
          <Route path="/team/member/:memberId/*" element={<TeamMemberCard />} />

          <Route path="/blog/:articleId" element={<Blog />} />

          {/* Accessory Routes */}
          <Route path="/accessory" element={<AccessoryWorkFlow />} />
          <Route path="/accessory/scan/:serialNumber" element={<AccessoryWorkFlow />} />

          <Route path="/delete" element={
            <ProtectedRoute>
              <DeleteUser />
            </ProtectedRoute>
          } />




          {/* TEAM PLATFORM ADDITION - Add team route - Only accessible to team plan users */}
          <Route path="/team/*" element={
            <TeamAdminRoute>
              <TeamDashboard />
            </TeamAdminRoute>
          } />

          {/* Template routes at root level (like Touch: /template/23108) - Only accessible to team plan users */}
          <Route path="/template/create" element={
            <TeamProtectedRoute>
              <TeamDashboard />
            </TeamProtectedRoute>
          } />
          <Route path="/template/:templateId" element={
            <TeamProtectedRoute>
              <TeamDashboard />
            </TeamProtectedRoute>
          } />

          {/* TEAM V2 PLATFORM - New team interface - Only accessible to team plan users */}
          <Route path="/team-v2/*" element={
            <TeamProtectedRoute>
              <TeamV2Dashboard />
            </TeamProtectedRoute>
          } />

          {/* ADMIN ROUTES - Admin only access */}
          <Route path="/admin/*" element={
            <AdminProtectedRoute>
              <AdminContainer user={authState?.user} />
            </AdminProtectedRoute>
          } />
        </Routes>
      </Router>
    </Sentry.ErrorBoundary>
  );
};

export default App;